import axios from 'axios';
import { notifyUnauthorized } from './authEvents';

// No default Content-Type: the upload goes as multipart (the browser adds the boundary). Queueing
// the job itself is fast regardless of file size - the import runs in the background - so this no
// longer needs the generous timeout a synchronous import did.
const apiClient = axios.create({
  baseURL: process.env.REACT_APP_API_BASE_URL || '',
  timeout: 30000
});

apiClient.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) notifyUnauthorized();
    return Promise.reject(error);
  }
);

// Error carrying the API's `detail` message when there is one
const toError = (error, fallbackMessage) => {
  const detail = error.response?.data?.detail;
  const apiError = new Error(typeof detail === 'string' ? detail : (error.message || fallbackMessage));
  apiError.status = error.response?.status;
  apiError.originalError = error;
  return apiError;
};

/**
 * Import a LinkedIn connections export: POST /api/v2/ingest queues it and returns a job id right
 * away; the import itself runs in the background (app/services/linkedin_import_jobs.py), in
 * batches, so poll jobStatus() for progress and, once status is "done", the result. Admin only.
 */
class LinkedInImportApiService {

  /**
   * @param {File} file - The advisor's Connections.csv (or a merged file with an Advisor column)
   * @param {string} [advisor] - Whose export it is; not needed when its rows name their advisor
   * @param {boolean} [dryRun] - Report what would change without writing anything
   * @returns {Promise<Object>} the queued job: id, status, dry_run, advisor, filename, created_at, ...
   */
  async ingest(file, advisor, dryRun = false) {
    try {
      const form = new FormData();
      form.append('file', file, file.name);
      const params = { dry_run: Boolean(dryRun) };
      const name = (advisor || '').trim();
      if (name) params.advisor = name;
      const { data } = await apiClient.post('/api/v2/ingest', form, { params });
      return data;
    } catch (error) {
      throw toError(error, 'Could not import the connections');
    }
  }

  /**
   * @param {number} jobId
   * @returns {Promise<Object>} status, phase, total_rows, processed_rows, and once status is
   *   "done" or "failed", result (success_count, error_count, message, dry_run, backup, advisors)
   *   or error
   */
  async jobStatus(jobId) {
    try {
      const { data } = await apiClient.get(`/api/v2/ingest/jobs/${jobId}`);
      return data;
    } catch (error) {
      throw toError(error, "Could not check the import's progress");
    }
  }

  /**
   * Recent import jobs, newest first - so the UI can find one still running after a page reload.
   * @param {number} [limit]
   */
  async recentJobs(limit = 5) {
    try {
      const { data } = await apiClient.get('/api/v2/ingest/jobs', { params: { limit } });
      return data;
    } catch (error) {
      throw toError(error, 'Could not load recent imports');
    }
  }
}

const linkedinImportApi = new LinkedInImportApiService();

export default linkedinImportApi;
